import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  InputLabel,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";

export const EstimationDialog = ({
  open,
  onClose,
  onSave,
  initialData = { name: "", packages: [], payments: [] },
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { darkMode } = useContext(ThemeContext);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialData?.name || "",
    },
  });

  const [treatmentRows, setTreatmentRows] = useState(() => {
    if (!initialData?.id && !initialData?.name?.startsWith("Copy of")) {
      return [{ name: "", visits: "", costPerTreatment: "", totalCost: "" }];
    }
    // Edit or Copy Mode: Display packages
    if (initialData?.packages?.length > 0) {
      return initialData.packages.map((pkg) => ({
        name: pkg.name,
        visits: pkg.visit,
        costPerTreatment: pkg.cost,
        totalCost: pkg.visit * pkg.cost,
      }));
    }
  });

  const handleTreatmentInputChange = (index, field, value) => {
    const updatedRows = [...treatmentRows];
    updatedRows[index][field] = value;

    if (field === "visits" || field === "costPerTreatment") {
      const visits = parseInt(updatedRows[index].visits, 10);
      const cost = parseFloat(updatedRows[index].costPerTreatment);
      if (!isNaN(visits) && !isNaN(cost)) {
        updatedRows[index].totalCost = visits * cost;
      } else {
        updatedRows[index].totalCost = "";
      }
    }

    setTreatmentRows(updatedRows);
  };

  const [paymentRows, setPaymentRows] = useState(() => {
    if (!initialData?.id && !initialData?.name?.startsWith("Copy of")) {
      return [];
    }

    if (initialData?.payments?.length > 0) {
      return initialData.payments.map((option) => ({
        number_of_payments: option.payments,
        discount: option.discount,
        upfront: option.upfront,
      }));
    }

    return [];
  });

  const handlePaymentInputChange = (index, field, value) => {
    const updatedRows = [...paymentRows];
    updatedRows[index][field] = value;

    // Recalculate dependent fields
    const numberOfPayments = parseInt(
      updatedRows[index].number_of_payments || 1,
      10
    );
    let upfront = parseFloat(updatedRows[index].upfront || 0);
    const discount = parseFloat(updatedRows[index].discount || 0);

    // Calculate totalCost dynamically
    const totalCost = calculateTotalPackageCost();

    // Calculate totalPayment
    const totalPayment = totalCost * (1 - discount / 100);
    updatedRows[index].totalPayment = totalPayment.toFixed(2);

    if (upfront > totalPayment) {
      updatedRows[index].upfront = totalPayment.toFixed(2);
      upfront = totalPayment.toFixed(2);
    }

    // Calculate monthly payment
    if (!isNaN(numberOfPayments) && numberOfPayments > 0) {
      const remainingCost = totalPayment - upfront;
      updatedRows[index].monthly = (remainingCost / numberOfPayments).toFixed(
        2
      );
    } else {
      updatedRows[index].monthly = "";
    }

    setPaymentRows(updatedRows);
  };

  const isEditMode = !!initialData?.id;
  const isCopyMode = initialData?.name?.startsWith("Copy of");
  const dialogTitle = isEditMode
    ? "Edit Template"
    : isCopyMode
    ? "Copy Template"
    : "Add Template";
  const submitButtonText = isEditMode ? "Save" : "Add";

  const isValidRow = (row) => {
    return (
      row.name &&
      row.visits &&
      row.costPerTreatment &&
      !isNaN(row.visits) &&
      !isNaN(row.costPerTreatment) &&
      row.visits >= 1 &&
      row.visits <= 999 &&
      row.costPerTreatment >= 0 &&
      row.costPerTreatment <= 9999
    );
  };

  const handleAddTreatmentRow = () => {
    // Check if any row in the table is invalid
    const invalidRowIndex = treatmentRows.findIndex((row) => !isValidRow(row));
    if (invalidRowIndex !== -1) {
      enqueueSnackbar(
        `Row ${
          invalidRowIndex + 1
        } is incomplete. Please fill out all fields before adding a new row.`,
        { variant: "error" }
      );
      return;
    }

    // Add a new empty row if all rows are valid
    setTreatmentRows([
      ...treatmentRows,
      { name: "", visits: "", costPerTreatment: "", totalCost: "" },
    ]);
  };

  const handleRemoveTreatmentRow = (index) => {
    // Remove the row from treatmentRows
    const updatedTreatmentRows = [...treatmentRows];
    updatedTreatmentRows.splice(index, 1);

    // If all rows are removed, add a default empty row
    if (updatedTreatmentRows.length === 0) {
      updatedTreatmentRows.push({
        name: "",
        visits: "",
        costPerTreatment: "",
        totalCost: "",
      });

      const updatedPaymentRows = [];
      setPaymentRows(updatedPaymentRows);
    }

    // Update the state
    setTreatmentRows(updatedTreatmentRows);
  };

  const handleAddPaymentRow = () => {
    const temp = 0;
    setPaymentRows([
      ...paymentRows,
      {
        number_of_payments: 1,
        discount: 0,
        upfront: temp.toFixed(2),
        monthly: temp.toFixed(2),
        totalPayment: calculateTotalPackageCost().toFixed(2),
      },
    ]);
  };

  const handleRemovePaymentRow = (index) => {
    // Remove the row from paymentRows
    const updatedPaymentRows = [...paymentRows];
    updatedPaymentRows.splice(index, 1);
    // If all rows are removed, add a default empty row

    // Update the state
    setPaymentRows(updatedPaymentRows);
  };

  const calculateTotalPackageCost = () => {
    const totalCost = treatmentRows.reduce((sum, row) => {
      const totalCost = parseFloat(row.totalCost);
      return !isNaN(totalCost) ? sum + totalCost : sum;
    }, 0);

    return totalCost;
  };

  const onSubmit = (data) => {
    if (treatmentRows.some((row) => !isValidRow(row))) {
      enqueueSnackbar(
        "All fields in the table are required and must be valid",
        {
          variant: "error",
        }
      );
      return;
    }

    onSave({
      id: initialData?.id,
      name: data.name,
      packages: treatmentRows.map((treatment, index) => ({
        name: treatment.name,
        visit: treatment.visits,
        cost: treatment.costPerTreatment,
      })),
      payments: paymentRows?.map((payment, index) => ({
        payments: payment.number_of_payments,
        discount: payment.discount,
        upfront: payment.upfront,
      })),
    });
    onClose();
  };

  const resetForm = () => {
    setTreatmentRows([
      { name: "", visits: "", costPerTreatment: "", totalCost: "" },
    ]); // Reset table rows
    setPaymentRows([]); // Reset table rows
    reset(); // Reset form fields (react-hook-form)
  };

  const handleDialogClose = () => {
    if (!isEditMode && !isCopyMode) {
      resetForm(); // Reset fields if not in edit mode
    }
    onClose();
  };

  useEffect(() => {
    if (open && !isEditMode && !isCopyMode) {
      resetForm(); // Reset fields when opening the dialog in Add Mode
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode]);

  useEffect(() => {
    const totalCost = calculateTotalPackageCost();

    const updatedPaymentRows = paymentRows?.map((row) => {
      const discount = parseFloat(row.discount) || 0;
      let upfront = parseFloat(row.upfront) || 0;
      const numberOfPayments = parseInt(row.number_of_payments, 10);

      // Recalculate totalPayment
      const totalPayment = totalCost * (1 - discount / 100);
      row.totalPayment = totalPayment.toFixed(2);

      if (upfront > totalPayment) {
        upfront = totalPayment.toFixed(2);
      }

      // Recalculate monthly payment
      if (!isNaN(numberOfPayments) && numberOfPayments > 0) {
        const remainingCost = totalPayment - upfront;
        row.monthly = (remainingCost / numberOfPayments).toFixed(2);
      } else {
        row.monthly = "";
      }

      return row;
    });

    setPaymentRows(updatedPaymentRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentRows]); // Dependency on treatmentRows

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="lg">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <TextField
            label="Template Name"
            placeholder="Enter Template name"
            {...register("name", {
              required: "Template Name is required",
              minLength: {
                value: 3,
                message: "Template Name must be at least 3 characters long",
              },
              maxLength: {
                value: 50,
                message: "Template Name cannot exceed 50 characters",
              },
            })}
            error={!!errors.name}
            helperText={errors.name?.message}
            fullWidth
            margin="normal"
          />

          <Box mt={2} display={{ xs: "none", md: "block" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                      width: "39%", // Name field is long
                    }}
                  >
                    Treatments
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                      width: "17%", // Visits field is short (3 digits)
                      textAlign: "center",
                    }}
                  >
                    Visits
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                      width: "18%", // Cost per Treatment field is medium (4 digits)
                      textAlign: "center",
                    }}
                  >
                    Cost Per Visit ($)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                      width: "21%", // Total Cost field is medium-long (7 digits)
                      textAlign: "center",
                    }}
                  >
                    Total ($)
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                      width: "5%", // Actions column is very short
                    }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treatmentRows.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TableCell
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "39%", // Matches the header width
                      }}
                    >
                      <TextField
                        value={row.name}
                        onChange={(e) =>
                          handleTreatmentInputChange(
                            index,
                            "name",
                            e.target.value
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "17%", // Matches the header width
                      }}
                    >
                      <TextField
                        type="number"
                        value={row.visits}
                        onChange={(e) =>
                          handleTreatmentInputChange(
                            index,
                            "visits",
                            e.target.value
                          )
                        }
                        fullWidth
                        inputProps={{ min: 1, max: 999 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "18%", // Matches the header width
                      }}
                    >
                      <TextField
                        type="number"
                        value={row.costPerTreatment}
                        onChange={(e) =>
                          handleTreatmentInputChange(
                            index,
                            "costPerTreatment",
                            e.target.value
                          )
                        }
                        fullWidth
                        inputProps={{ min: 0, max: 9999 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "21%", // Matches the header width
                      }}
                    >
                      <TextField
                        value={row.totalCost}
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "5%", // Matches the header width
                      }}
                    >
                      <IconButton
                        onClick={() => handleRemoveTreatmentRow(index)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Package Cost Row */}
                <TableRow
                  sx={{
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: darkMode
                      ? getColors.drawerTextDark
                      : getColors.thumbnailBorderLight,
                  }}
                >
                  <TableCell
                    colSpan={3}
                    align="right"
                    sx={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                      Total Plan Cost ($)
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                    }}
                  >
                    <TextField
                      value={calculateTotalPackageCost().toFixed(2)}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailBorderLight,
                    }}
                  />
                </TableRow>
              </TableBody>
            </Table>
            <Box mt={2} textAlign="center">
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={handleAddTreatmentRow}
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Add Treatment
              </Button>
            </Box>
          </Box>

          <Box mt={2} display={{ xs: "none", md: "block" }}>
            <InputLabel>Payment Options</InputLabel>
            {paymentRows?.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "19%", // Name field is long
                      }}
                    >
                      Number of Payments
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "19%", // Visits field is short (3 digits)
                        textAlign: "center",
                      }}
                    >
                      Discount (%)
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "19%", // Cost per Treatment field is medium (4 digits)
                        textAlign: "center",
                      }}
                    >
                      Upfront Payment ($)
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "19%", // Total Cost field is medium-long (7 digits)
                        textAlign: "center",
                      }}
                    >
                      Monthly ($)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "19%", // Actions column is very short
                      }}
                    >
                      Total ($)
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        fontSize: "18px",
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                        width: "5%", // Actions column is very short
                      }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentRows.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      <TableCell
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "19%", // Matches the header width
                        }}
                      >
                        <TextField
                          type="number"
                          value={row.number_of_payments}
                          onChange={(e) =>
                            handlePaymentInputChange(
                              index,
                              "number_of_payments",
                              e.target.value
                            )
                          }
                          fullWidth
                          inputProps={{ min: 1, max: 12 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "19%", // Matches the header width
                        }}
                      >
                        <TextField
                          type="number"
                          value={row.discount}
                          onChange={(e) =>
                            handlePaymentInputChange(
                              index,
                              "discount",
                              e.target.value
                            )
                          }
                          fullWidth
                          inputProps={{ min: 0, max: 100 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "19%", // Matches the header width
                        }}
                      >
                        <TextField
                          type="number"
                          value={row.upfront}
                          onChange={(e) =>
                            handlePaymentInputChange(
                              index,
                              "upfront",
                              e.target.value
                            )
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "19%", // Matches the header width
                        }}
                      >
                        <TextField
                          value={row.monthly}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "19%", // Matches the header width
                        }}
                      >
                        <TextField
                          value={row.totalPayment}
                          fullWidth
                          InputProps={{ readOnly: true }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          borderWidth: 1,
                          borderStyle: "solid",
                          borderColor: darkMode
                            ? getColors.drawerTextDark
                            : getColors.thumbnailBorderLight,
                          width: "5%", // Matches the header width
                        }}
                      >
                        <IconButton
                          onClick={() => handleRemovePaymentRow(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Box mt={2} textAlign="center">
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={handleAddPaymentRow}
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Add Payment Option
              </Button>
            </Box>
          </Box>

          <Box mt={1} display={{ xs: "block", md: "none" }}>
            {treatmentRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${
                    darkMode
                      ? getColors.drawerTextDark
                      : getColors.thumbnailBorderLight
                  }`,
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    Treatment:
                  </Typography>
                  <TextField
                    value={row.name}
                    onChange={(e) =>
                      handleTreatmentInputChange(index, "name", e.target.value)
                    }
                    fullWidth
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", sm: "row" }}
                  mt={2}
                  gap={2}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={{ xs: "100%", sm: "50%" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                      }}
                    >
                      Visits:
                    </Typography>
                    <TextField
                      type="number"
                      value={row.visits}
                      onChange={(e) =>
                        handleTreatmentInputChange(
                          index,
                          "visits",
                          e.target.value
                        )
                      }
                      fullWidth
                      inputProps={{ min: 1, max: 999 }}
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={{ xs: "100%", sm: "50%" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                      }}
                    >
                      Cost:
                    </Typography>
                    <TextField
                      type="number"
                      value={row.costPerTreatment}
                      onChange={(e) =>
                        handleTreatmentInputChange(
                          index,
                          "costPerTreatment",
                          e.target.value
                        )
                      }
                      fullWidth
                      inputProps={{ min: 0, max: 9999 }}
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  mt={2}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    Total:
                  </Typography>
                  <TextField
                    value={row.totalCost}
                    InputProps={{ readOnly: true }}
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemoveTreatmentRow(index)}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            ))}

            {/* Total Package Cost Section */}
            <Box
              sx={{
                border: `1px solid ${
                  darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailBorderLight
                }`,
                borderRadius: "8px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "16px",
                    color: darkMode
                      ? getColors.drawerTextDark
                      : getColors.thumbnailTextLight,
                  }}
                >
                  Total Plan Cost:
                </Typography>
                <TextField
                  value={calculateTotalPackageCost().toFixed(2)}
                  InputProps={{ readOnly: true }}
                  sx={{ ml: 2 }}
                />
              </Box>
            </Box>

            <Box mt={2} textAlign="center">
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={handleAddTreatmentRow}
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Add Treatment
              </Button>
            </Box>
          </Box>

          <Box mt={1} display={{ xs: "block", md: "none" }}>
            {paymentRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${
                    darkMode
                      ? getColors.drawerTextDark
                      : getColors.thumbnailBorderLight
                  }`,
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", sm: "row" }}
                  mt={2}
                  gap={2}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={{ xs: "100%", sm: "50%" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                      }}
                    >
                      Number:
                    </Typography>
                    <TextField
                      type="number"
                      value={row.number_of_payments}
                      onChange={(e) =>
                        handlePaymentInputChange(
                          index,
                          "number_of_payments",
                          e.target.value
                        )
                      }
                      fullWidth
                      inputProps={{ min: 1, max: 12 }}
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={{ xs: "100%", sm: "50%" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                      }}
                    >
                      Discount:
                    </Typography>
                    <TextField
                      type="number"
                      value={row.discount}
                      onChange={(e) =>
                        handlePaymentInputChange(
                          index,
                          "discount",
                          e.target.value
                        )
                      }
                      fullWidth
                      inputProps={{ min: 0, max: 100 }}
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width={{ xs: "100%", sm: "50%" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: darkMode
                          ? getColors.drawerTextDark
                          : getColors.thumbnailTextLight,
                      }}
                    >
                      Upfront:
                    </Typography>
                    <TextField
                      type="number"
                      value={row.upfront}
                      onChange={(e) =>
                        handlePaymentInputChange(
                          index,
                          "upfront",
                          e.target.value
                        )
                      }
                      fullWidth
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  mt={2}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    Monthly:
                  </Typography>
                  <TextField
                    value={row.monthly}
                    InputProps={{ readOnly: true }}
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  mt={2}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    Total:
                  </Typography>
                  <TextField
                    value={row.totalPayment}
                    InputProps={{ readOnly: true }}
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleRemovePaymentRow(index)}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            ))}

            <Box mt={2} textAlign="center">
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={handleAddPaymentRow}
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                Add Payment Option
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pr: 3 }}>
          <Button
            onClick={onClose}
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "18px" },
              fontWeight: 500,
              color: darkMode
                ? getColors.appBarBgLight
                : getColors.appBarBgDark,
              textAlign: "center",
              "&: hover": {
                background: "none",
                color: getColors.primaryMain,
              },
              mr: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "18px" },
              fontWeight: 500,
              textAlign: "center",
              px: 5,
            }}
          >
            {submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
